/**
 * The light/dark colour mode — one store for every toggle and every link that
 * has to know it.
 *
 * The mode is an explicit choice (no system-preference fallback), applied as
 * `data-theme="dark"` on <html>, which is what `styles/tokens.css` keys its
 * dark palette off. It is shared with the web shop, which lives on another
 * domain, so a cookie cannot carry it across; instead
 *  - every link into the shop carries `?theme=light|dark` (withThemeParam),
 *  - a `?theme=` on any URL this app is opened with is adopted (persisted and
 *    stripped from the address bar) — consumeThemeFromUrl, which the shop
 *    mirrors on its side,
 *  - the choice is persisted in localStorage AND a `wh-theme` cookie on this
 *    site, so it survives a cleared storage and is readable server-side.
 * index.html applies the same three sources before first paint, so a dark page
 * never flashes light.
 *
 * A small external store (useSyncExternalStore) rather than a context: the
 * toggle renders in more than one place and the sidebar's shop links must
 * re-render with the new choice, without threading a provider through the
 * tree.
 */

import { useSyncExternalStore } from 'react'

export type Theme = 'light' | 'dark'

/** Same key as the web shop, for storage, the cookie and the URL contract. */
export const THEME_STORAGE_KEY = 'wh-theme'
export const THEME_COOKIE_NAME = 'wh-theme'
/** Query parameter both apps use to hand the choice across the domain boundary. */
export const THEME_QUERY_PARAM = 'theme'

const THEME_ATTRIBUTE = 'data-theme'
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365

const listeners = new Set<() => void>()
let currentTheme: Theme | null = null

export function isTheme(value: unknown): value is Theme {
  return value === 'light' || value === 'dark'
}

function readDocumentTheme(): Theme | null {
  if (typeof document === 'undefined') return null
  return document.documentElement.getAttribute(THEME_ATTRIBUTE) === 'dark' ? 'dark' : null
}

function readLocalStorageTheme(): Theme | null {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY)
    return isTheme(stored) ? stored : null
  } catch {
    return null
  }
}

export function readCookieTheme(cookieString: string = document.cookie): Theme | null {
  const match = cookieString
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${THEME_COOKIE_NAME}=`))
  if (!match) return null
  const value = decodeURIComponent(match.slice(THEME_COOKIE_NAME.length + 1))
  return isTheme(value) ? value : null
}

/**
 * The current choice: what index.html already stamped on the document, else
 * localStorage, else the cookie; light when none holds a valid value.
 */
export function readStoredTheme(): Theme {
  if (typeof window === 'undefined') return 'light'
  return readDocumentTheme() ?? readLocalStorageTheme() ?? readCookieTheme() ?? 'light'
}

function persistTheme(theme: Theme): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, theme)
  } catch {
    // Storage unavailable (private mode, quota): the cookie still carries it.
  }
  try {
    const secure = window.location.protocol === 'https:' ? '; Secure' : ''
    document.cookie = `${THEME_COOKIE_NAME}=${theme}; Path=/; Max-Age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax${secure}`
  } catch {
    // Cookies disabled: the choice still applies for this page view.
  }
}

/** Sets or clears the attribute the dark stylesheet keys off. */
export function applyThemeToDocument(theme: Theme, doc: Document = document): void {
  if (theme === 'dark') doc.documentElement.setAttribute(THEME_ATTRIBUTE, 'dark')
  else doc.documentElement.removeAttribute(THEME_ATTRIBUTE)
}

export function getTheme(): Theme {
  if (currentTheme === null) currentTheme = readStoredTheme()
  return currentTheme
}

export function setTheme(theme: Theme): void {
  currentTheme = theme
  persistTheme(theme)
  applyThemeToDocument(theme)
  listeners.forEach((listener) => listener())
}

export function toggleTheme(): void {
  setTheme(getTheme() === 'dark' ? 'light' : 'dark')
}

export function subscribeTheme(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Appends the theme to a URL the other app will open, keeping any query it has. */
export function withThemeParam(url: string, theme: Theme): string {
  const hashIndex = url.indexOf('#')
  const base = hashIndex === -1 ? url : url.slice(0, hashIndex)
  const hash = hashIndex === -1 ? '' : url.slice(hashIndex)
  const separator = base.includes('?') ? '&' : '?'
  return `${base}${separator}${THEME_QUERY_PARAM}=${theme}${hash}`
}

/**
 * Adopts a `?theme=` the page was opened with (the shop handing the choice
 * over), persists it and strips it from the address bar so it is not carried
 * into every later link or bookmark. Returns the adopted theme, or null when
 * the URL carried none. Idempotent: index.html does the same before the bundle
 * loads (the router must never see the parameter), and this is the tested
 * fallback for a shell whose index.html does not.
 */
export function consumeThemeFromUrl(win: Window = window): Theme | null {
  const params = new URLSearchParams(win.location.search)
  const value = params.get(THEME_QUERY_PARAM)
  if (!isTheme(value)) return null

  setTheme(value)

  params.delete(THEME_QUERY_PARAM)
  const search = params.toString()
  const cleaned = `${win.location.pathname}${search ? `?${search}` : ''}${win.location.hash}`
  win.history.replaceState(win.history.state, '', cleaned)
  return value
}

/** Test seam: forget the cached choice so the next read hits the document/storage again. */
export function resetThemeForTests(): void {
  currentTheme = null
}

function getServerTheme(): Theme {
  return 'light'
}

export function useTheme() {
  const theme = useSyncExternalStore(subscribeTheme, getTheme, getServerTheme)
  return {
    theme,
    isDark: theme === 'dark',
    setTheme,
    toggleTheme,
  }
}
